import React, { useState, useEffect, useRef } from 'react';
import { Canvas, useFrame } from '@react-three/fiber';
import { Points, PointMaterial } from '@react-three/drei';
import * as random from 'maath/random';

const StarParticles = ({ color }) => {
  const ref = useRef();
  const [positions] = useState(() => 
    new Float32Array(
      random.inSphere(new Float32Array(15000), { radius: 1.5 })
  ));

  useFrame((_, delta) => {
    if (ref.current) {
      ref.current.rotation.x -= delta / 10;
      ref.current.rotation.y -= delta / 15;
    }
  });

  return (
    <Points ref={ref} positions={positions} stride={3}>
      <PointMaterial
        transparent
        color={color}
        size={0.003}
        sizeAttenuation={true}
        depthWrite={false}
      />
    </Points>
  );
};

const StarsCanvas = ({ color }) => {
  return (
    <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100vh', zIndex: -1 }}>
      <Canvas camera={{ position: [0, 0, 1] }}>
        <StarParticles color={color} />
      </Canvas>
    </div>
  );
};

function BackgroundPattern() {
  const [background, setBackground] = useState('');

  const updateBackground = () => {
    const currentHour = new Date().getHours();
    const gradientEffects = [
      { hour: 0, gradient: 'linear-gradient(to bottom, #00000c 100%,#00000c 100%)' },
      { hour: 1, gradient: 'linear-gradient(to bottom, #020111 85%,#191621 100%)' },
      { hour: 2, gradient: 'linear-gradient(to bottom, #020111 60%,#20202c 100%)' },
      { hour: 3, gradient: 'linear-gradient(to bottom, #020111 10%,#3a3a52 100%)' },
      { hour: 4, gradient: 'linear-gradient(to bottom, #20202c 0%,#515175 100%)' },
      { hour: 5, gradient: 'linear-gradient(to bottom, #40405c 0%,#6f71aa 80%,#8a76ab 100%)' },
      { hour: 6, gradient: 'linear-gradient(to bottom, #4a4969 0%,#7072ab 50%,#cd82a0 100%)' },
      { hour: 7, gradient: 'linear-gradient(to bottom, #757abf 0%,#8583be 60%,#eab0d1 100%)' },
      { hour: 8, gradient: 'linear-gradient(to bottom, #82addb 0%,#ebb2b1 100%)' },
      { hour: 9, gradient: 'linear-gradient(to bottom, #94c5f8 1%,#a6e6ff 70%,#b1b5ea 100%)' },
      { hour: 10, gradient: 'linear-gradient(to bottom, #b7eaff 0%,#94dfff 100%)' },
      { hour: 11, gradient: 'linear-gradient(to bottom, #9be2fe 0%,#67d1fb 100%)' },
      { hour: 12, gradient: 'linear-gradient(to bottom, #90dffe 0%,#38a3d1 100%)' },
      { hour: 13, gradient: 'linear-gradient(to bottom, #57c1eb 0%,#246fa8 100%)' },
      { hour: 14, gradient: 'linear-gradient(to bottom, #2d91c2 0%,#1e528e 100%)' },
      { hour: 15, gradient: 'linear-gradient(to bottom, #2473ab 0%,#1e528e 70%,#5b7983 100%)' },
      { hour: 16, gradient: 'linear-gradient(to bottom, #1e528e 0%,#265889 50%,#9da671 100%)' },
      { hour: 17, gradient: 'linear-gradient(to bottom, #1e528e 0%,#728a7c 50%,#e9ce5d 100%)' },
      { hour: 18, gradient: 'linear-gradient(to bottom, #154277 0%,#576e71 30%,#e1c45e 70%,#b26339 100%)' },
      { hour: 19, gradient: 'linear-gradient(to bottom, #163C52 0%,#4F4F47 30%,#C5752D 60%,#B7490F 80%, #2F1107 100%)' },
      { hour: 20, gradient: 'linear-gradient(to bottom, #071B26 0%,#071B26 30%,#8A3B12 80%,#240E03 100%)' },
      { hour: 21, gradient: 'linear-gradient(to bottom, #010A10 30%,#59230B 80%,#2F1107 100%)' },
      { hour: 22, gradient: 'linear-gradient(to bottom, #090401 50%,#4B1D06 100%)' },
      { hour: 23, gradient: 'linear-gradient(to bottom, #00000c 80%,#150800 100%)' },
    ];

    const gradientEffect = gradientEffects.find(effect => effect.hour === currentHour);
    if (gradientEffect) {
      setBackground(gradientEffect.gradient);
    }
  };

  useEffect(() => {
    updateBackground();
    const intervalId = setInterval(updateBackground, 60000);
    return () => clearInterval(intervalId);
  }, []);

  const currentHour = new Date().getHours();
  const finalTextColor = currentHour >= 8 && currentHour < 13 ? '#000' : '#fff';

  return (
    <>
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100%',
          height: '100vh',
          background,
          filter: 'brightness(0.9)',
          zIndex: -2,
        }}
      />
      
      <StarsCanvas color={finalTextColor} />
      
      <style>
        {`
          body {
            margin: 0;
            color: ${finalTextColor};
            overflow-x: hidden;
          }
        `}
      </style>
    </>
  );
}

export default BackgroundPattern;